
import { 
  Timestamp, 
  getDocs, 
  getDoc,
  collection, 
  addDoc, 
  setDoc, 
  doc, 
  updateDoc, 
  deleteDoc 
} from "firebase/firestore";
import { 
  ref, 
  uploadString, 
  uploadBytes,
  getDownloadURL, 
  deleteObject 
} from "firebase/storage";
import { db, storage } from "./firebase";

// --- Firestore Helper Functions ---

/**
 * Converts Date objects in data to Firestore Timestamps for proper storage.
 * Also recursively sanitizes nested objects, stripping 'undefined', functions, or invalid fields.
 */
const convertDatesToTimestamps = (data: any): any => {
  if (data === null || data === undefined) return null;
  if (data instanceof Date) return Timestamp.fromDate(data);
  if (data instanceof Timestamp) return data;
  
  if (Array.isArray(data)) {
    return data
      .filter((item: any) => item !== undefined && typeof item !== 'function' && typeof item !== 'symbol')
      .map((item: any) => (typeof item === 'object' && item !== null ? convertDatesToTimestamps(item) : item));
  }
  
  if (typeof data === 'object') {
    const newData: any = {};
    for (const key of Object.keys(data)) {
      const value = data[key];
      if (value === undefined || typeof value === 'function' || typeof value === 'symbol') {
        continue;
      }
      if (value instanceof Date) {
        newData[key] = Timestamp.fromDate(value);
      } else if (value instanceof Timestamp) {
        newData[key] = value;
      } else if (typeof value === 'object' && value !== null) {
        newData[key] = convertDatesToTimestamps(value);
      } else {
        newData[key] = value;
      }
    }
    return newData;
  }
  
  return data;
};

/**
 * Converts Firestore Timestamps in data to JavaScript Date objects.
 */
const convertTimestampsToDates = (data: any) => {
  const newData: any = {};
  for (const key in data) {
    if (data[key] instanceof Timestamp) {
      newData[key] = data[key].toDate();
    } else if (
      typeof data[key] === "object" &&
      data[key] !== null &&
      !Array.isArray(data[key])
    ) {
      newData[key] = convertTimestampsToDates(data[key]);
    } else if (Array.isArray(data[key])) {
        newData[key] = data[key].map((item: any) => {
            if (typeof item === 'object' && item !== null) {
                return convertTimestampsToDates(item);
            }
            return item;
        });
    }
    else {
      newData[key] = data[key];
    }
  }
  return newData;
};

/**
 * Fetches all documents from a specified Firestore collection.
 * @param collectionName The name of the collection.
 * @returns A promise that resolves to an array of documents with their IDs.
 */
export const getCollection = async <T>(collectionName: string): Promise<T[]> => {
  try {
    const querySnapshot = await getDocs(collection(db, collectionName));
    return querySnapshot.docs.map(
      (doc) =>
        ({
          id: doc.id,
          ...convertTimestampsToDates(doc.data()),
        } as T)
    );
  } catch (error) {
    console.error(`Error getting collection ${collectionName}:`, error);
    return [];
  }
};

/**
 * Adds a new document to a specified Firestore collection.
 * @param collectionName The name of the collection.
 * @param data The data for the new document.
 * @returns A promise that resolves to the newly created document with its ID.
 */
export const addDocument = async <T extends {id: string}>(
  collectionName: string,
  data: Omit<T, "id">
): Promise<T> => {
  const docRef = await addDoc(
    collection(db, collectionName),
    convertDatesToTimestamps(data)
  );
  return { id: docRef.id, ...data } as T;
};

/**
 * Sets a document with a specific ID in a specified Firestore collection.
 * @param collectionName The name of the collection.
 * @param docId The ID of the document.
 * @param data The data for the document.
 */
export const setDocument = async (
  collectionName: string,
  docId: string,
  data: any
): Promise<void> => {
  const docRef = doc(db, collectionName, docId);
  await setDoc(docRef, convertDatesToTimestamps(data));
};

/**
 * Updates an existing document in a specified Firestore collection.
 * @param collectionName The name of the collection.
 * @param docId The ID of the document to update.
 * @param data The data to update.
 */
export const updateDocument = async <T>(
  collectionName: string,
  docId: string,
  data: Partial<T>
): Promise<void> => {
  const docRef = doc(db, collectionName, docId);
  await updateDoc(docRef, convertDatesToTimestamps(data));
};

/**
 * Deletes a document from a specified Firestore collection.
 * @param collectionName The name of the collection.
 * @param docId The ID of the document to delete.
 */
export const deleteDocument = async (
  collectionName: string,
  docId: string
): Promise<void> => {
  const docRef = doc(db, collectionName, docId);
  await deleteDoc(docRef);
};

/**
 * Gets a single document by ID from a specified collection.
 */
export const getDocument = async <T>(collectionName: string, docId: string): Promise<T | null> => {
  try {
    const docRef = doc(db, collectionName, docId);
    const snap = await getDoc(docRef);
    if (!snap.exists()) return null;
    return { id: snap.id, ...convertTimestampsToDates(snap.data()) } as T;
  } catch (error) {
    console.error(`Error getting document ${collectionName}/${docId}:`, error);
    return null;
  }
};

/**
 * Stores the organization's custom Gemini API key securely in a protected sub-collection.
 */
export const saveOrgApiKey = async (orgId: string, apiKey?: string): Promise<void> => {
  if (!orgId) return;
  const keyDocRef = doc(db, 'organizations', orgId, 'secure_keys', 'gemini');
  if (!apiKey || !apiKey.trim()) {
    try {
      await deleteDoc(keyDocRef);
    } catch (e) {
      // Document might already not exist
    }
  } else {
    await setDoc(keyDocRef, {
      apiKey: apiKey.trim(),
      updatedAt: Timestamp.fromDate(new Date())
    });
  }
};

/**
 * Retrieves the organization's custom Gemini API key from the protected sub-collection.
 */
export const getOrgApiKey = async (orgId: string): Promise<string | null> => {
  if (!orgId) return null;
  try {
    const keyDocRef = doc(db, 'organizations', orgId, 'secure_keys', 'gemini');
    const snap = await getDoc(keyDocRef);
    if (snap.exists()) {
      const data = snap.data();
      return (data?.apiKey as string)?.trim() || null;
    }
    return null;
  } catch (e) {
    // If not accessible or not present, return null safely
    return null;
  }
};


// --- Firebase Storage Helper Functions ---

/**
 * Uploads a base64 encoded image string to Firebase Storage.
 * @param base64String The base64 image data.
 * @param path The path in Firebase Storage to upload the file to (e.g., 'profile_images/user123.jpg').
 * @returns A promise that resolves to the public download URL of the uploaded image.
 */
export const uploadImageFromBase64 = async (
  base64String: string,
  path: string
): Promise<string> => {
  if (!base64String) return base64String;

  try {
    const storageRef = ref(storage, path);

    if (base64String.startsWith('data:')) {
      // If it's a data URL (e.g. "data:image/jpeg;base64,..."), use 'data_url' format.
      // This automatically extracts the Content-Type from the prefix, ensuring security rules pass.
      await uploadString(storageRef, base64String, "data_url");
    } else {
      // Fallback for raw base64 strings (no prefix).
      // We manually set a generic image type to help pass security rules that check contentType.
      await uploadString(storageRef, base64String, "base64", { contentType: 'image/png' });
    }
    
    const downloadURL = await getDownloadURL(storageRef);
    return downloadURL;
  } catch (error) {
    console.warn("Firebase Storage upload encountered an error (or unauthorized), safely falling back to inline data URL:", error);
    // Return original base64/data URL so the document creation and UI render without failing
    return base64String;
  }
};

/**
 * Deletes a file from Firebase Storage using its public download URL.
 * @param fileUrl The full HTTPS download URL of the file to delete.
 */
export const deleteFileFromStorage = async (fileUrl: string): Promise<void> => {
  if (!fileUrl || !fileUrl.startsWith('https://firebasestorage.googleapis.com')) {
    console.warn(`Invalid or empty file URL, skipping deletion: ${fileUrl}`);
    return;
  }

  try {
    const storageRef = ref(storage, fileUrl);
    await deleteObject(storageRef);
  } catch (error: any) {
    // It's okay if the file doesn't exist (e.g., already deleted).
    if (error.code === 'storage/object-not-found') {
      console.warn(`File not found in storage, skipping deletion: ${fileUrl}`);
      return;
    }
    console.error("Error deleting file from storage:", error);
    throw error;
  }
};

/**
 * Whitelist and blacklist definitions for secure attachments across the app.
 */
export const ALLOWED_DOC_MIME_TYPES = [
  'image/jpeg',
  'image/png',
  'image/webp',
  'image/gif',
  'application/pdf',
  'application/msword',
  'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
  'text/plain'
];

export const ALLOWED_CHAT_MIME_TYPES = [
  'image/jpeg',
  'image/png',
  'image/webp',
  'image/gif',
  'application/pdf'
];

export const BLOCKED_EXTENSIONS = [
  '.exe', '.bat', '.cmd', '.sh', '.vbs', '.js', '.mjs', '.html', '.htm', 
  '.svg', '.php', '.py', '.apk', '.jar', '.com', '.scr', '.pif', '.msi'
];

/**
 * Generalized validator for attachments across homework, chat, broadcasts, and absence documentation.
 */
export const validateAttachmentFile = (
  file: File,
  options: {
    maxSizeBytes?: number;
    allowedMimeTypes?: string[];
    allowedExtensionsText?: string;
  } = {}
): { valid: boolean; error?: string } => {
  if (!file) return { valid: false, error: "No file selected." };

  const maxSizeBytes = options.maxSizeBytes || 10 * 1024 * 1024;
  const maxMb = (maxSizeBytes / (1024 * 1024)).toFixed(0);

  // 1. File Size check
  if (file.size > maxSizeBytes) {
    return { 
      valid: false, 
      error: `File "${file.name}" is too large (${(file.size / (1024 * 1024)).toFixed(1)}MB). The maximum allowed size is ${maxMb}MB.` 
    };
  }

  // 2. Extension check against malicious file types
  const lowerName = file.name.toLowerCase();
  const hasBlockedExt = BLOCKED_EXTENSIONS.some(ext => lowerName.endsWith(ext));
  if (hasBlockedExt) {
    return {
      valid: false,
      error: `File "${file.name}" has a disallowed file type. Executable, script, HTML, and SVG files are blocked for security.`
    };
  }

  // 3. MIME type / extension whitelist check
  const allowedTypes = options.allowedMimeTypes || ALLOWED_DOC_MIME_TYPES;
  const isAllowedType = allowedTypes.includes(file.type) || 
    (lowerName.endsWith('.pdf') && (file.type === '' || file.type === 'application/pdf')) ||
    (allowedTypes.includes('application/msword') && lowerName.endsWith('.docx') && (file.type === '' || file.type.includes('word') || file.type.includes('officedocument'))) ||
    (allowedTypes.includes('application/msword') && lowerName.endsWith('.doc') && (file.type === '' || file.type.includes('msword') || file.type.includes('word'))) ||
    (allowedTypes.includes('text/plain') && lowerName.endsWith('.txt') && (file.type === '' || file.type.includes('text')));

  if (!isAllowedType) {
    return {
      valid: false,
      error: `File type not supported for "${file.name}". ${options.allowedExtensionsText || 'Please attach images (JPG, PNG, WEBP), PDFs, or Word documents.'}`
    };
  }

  return { valid: true };
};

/**
 * Validates a file before uploading as homework attachment (max 10MB).
 */
export const validateHomeworkFile = (file: File): { valid: boolean; error?: string } => {
  return validateAttachmentFile(file, {
    maxSizeBytes: 10 * 1024 * 1024,
    allowedMimeTypes: ALLOWED_DOC_MIME_TYPES,
    allowedExtensionsText: 'Please attach images (JPG, PNG, WEBP), PDFs, or Word documents (.doc, .docx).'
  });
};

/**
 * Validates a chat or message attachment (max 5MB, images & PDFs only).
 */
export const validateChatAttachmentFile = (file: File): { valid: boolean; error?: string } => {
  return validateAttachmentFile(file, {
    maxSizeBytes: 5 * 1024 * 1024,
    allowedMimeTypes: ALLOWED_CHAT_MIME_TYPES,
    allowedExtensionsText: 'In chat, please attach images (JPG, PNG, WEBP) or PDFs under 5MB.'
  });
};

/**
 * Uploads any validated attachment (image, PDF, doc) directly to Firebase Cloud Storage.
 */
export const uploadAttachmentFile = async (
  file: File,
  folder: string = 'attachments',
  validationOptions?: { maxSizeBytes?: number; allowedMimeTypes?: string[]; allowedExtensionsText?: string }
): Promise<string> => {
  const validation = validateAttachmentFile(file, validationOptions);
  if (!validation.valid) {
    throw new Error(validation.error);
  }

  const safeFileName = file.name.replace(/[^a-zA-Z0-9._-]/g, '_');
  const uniquePath = `${folder}/${Date.now()}_${Math.random().toString(36).substring(2, 8)}_${safeFileName}`;
  const storageRef = ref(storage, uniquePath);

  // Derive explicit content type so Firebase Storage rules validate it correctly
  let contentType = file.type;
  if (!contentType) {
    const lower = file.name.toLowerCase();
    if (lower.endsWith('.pdf')) contentType = 'application/pdf';
    else if (lower.endsWith('.jpg') || lower.endsWith('.jpeg')) contentType = 'image/jpeg';
    else if (lower.endsWith('.png')) contentType = 'image/png';
    else if (lower.endsWith('.webp')) contentType = 'image/webp';
    else if (lower.endsWith('.docx')) contentType = 'application/vnd.openxmlformats-officedocument.wordprocessingml.document';
    else if (lower.endsWith('.doc')) contentType = 'application/msword';
    else if (lower.endsWith('.txt')) contentType = 'text/plain';
  }

  const metadata = {
    contentType: contentType || 'application/octet-stream',
    customMetadata: {
      originalName: file.name,
      uploadedAt: new Date().toISOString()
    }
  };

  await uploadBytes(storageRef, file, metadata);
  const downloadUrl = await getDownloadURL(storageRef);
  return downloadUrl;
};

/**
 * Uploads a homework attachment (image, PDF, doc) directly to Firebase Cloud Storage.
 * @param file The File object from an <input type="file">
 * @param folder The storage folder (default: 'homework')
 * @returns A promise resolving to the public download URL
 */
export const uploadHomeworkAttachment = async (
  file: File,
  folder: string = 'homework'
): Promise<string> => {
  return uploadAttachmentFile(file, folder, {
    maxSizeBytes: 10 * 1024 * 1024,
    allowedMimeTypes: ALLOWED_DOC_MIME_TYPES
  });
};

